/**
 * NIST Post-Quantum Cryptography Implementation
 *
 * This module implements the three NIST-standardized post-quantum algorithms:
 * - FIPS 203: ML-KEM (Module-Lattice-Based Key-Encapsulation Mechanism)
 * - FIPS 204: ML-DSA (Module-Lattice-Based Digital Signature Algorithm)
 * - FIPS 205: SLH-DSA (Stateless Hash-Based Digital Signature Algorithm)
 *
 * Security levels:
 * - Category 3: ~AES-192 equivalent quantum resistance
 * - Category 5: ~AES-256 equivalent quantum resistance
 */

import { ml_kem768, ml_kem1024 } from '@noble/post-quantum/ml-kem';
import { ml_dsa65, ml_dsa87 } from '@noble/post-quantum/ml-dsa';
import { slh_dsa_sha2_192f, slh_dsa_sha2_256f } from '@noble/post-quantum/slh-dsa';
import type { SecurityLevel, NISTAlgorithms, QuantumKeyPair, EncryptedData, SignatureData } from '@/types/quantum';
import { toBufferSource, ensureArrayBuffer } from '@/utils/crypto';

/**
 * NIST Post-Quantum Cryptography Engine
 *
 * Provides unified interface for all three NIST PQ standards with automatic
 * security level selection and algorithm coordination.
 */
export class NISTQuantumSafe {
  private securityLevel: SecurityLevel;
  private algorithms: NISTAlgorithms;

  constructor(securityLevel: SecurityLevel = 5) {
    this.securityLevel = securityLevel;
    this.algorithms = this.initializeAlgorithms(securityLevel);
  }

  /**
   * Initialize algorithm suite based on security level
   */
  private initializeAlgorithms(level: SecurityLevel): NISTAlgorithms {
    switch (level) {
      case 3:
        return {
          kem: ml_kem768,
          dsa: ml_dsa65,
          slh: slh_dsa_sha2_192f,
          securityLevel: 3,
          description: 'AES-192 equivalent quantum resistance'
        };
      case 5:
        return {
          kem: ml_kem1024,
          dsa: ml_dsa87,
          slh: slh_dsa_sha2_256f,
          securityLevel: 5,
          description: 'AES-256 equivalent quantum resistance'
        };
      default:
        throw new Error(`Unsupported security level: ${level}. Use 3 or 5.`);
    }
  }

  /**
   * Generate quantum-safe key pair for key encapsulation
   *
   * Uses ML-KEM (FIPS 203) for quantum-resistant key establishment
   */
  generateKEMKeys(): QuantumKeyPair {
    try {
      const keyPair = this.algorithms.kem.keygen();

      return {
        publicKey: keyPair.publicKey,
        privateKey: keyPair.secretKey,
        algorithm: 'ML-KEM',
        securityLevel: this.securityLevel,
        keySize: keyPair.publicKey.length,
        timestamp: Date.now()
      };
    } catch (error) {
      throw new Error(`ML-KEM key generation failed: ${error}`);
    }
  }

  /**
   * Generate quantum-safe signing key pair
   *
   * Uses ML-DSA (FIPS 204) as primary signing algorithm
   */
  generateDSAKeys(): QuantumKeyPair {
    try {
      const keyPair = this.algorithms.dsa.keygen();


      return {
        publicKey: keyPair.publicKey,
        privateKey: keyPair.secretKey,
        algorithm: 'ML-DSA',
        securityLevel: this.securityLevel,
        keySize: keyPair.publicKey.length,
        timestamp: Date.now()
      };
    } catch (error) {
      throw new Error(`ML-DSA key generation failed: ${error}`);
    }
  }

  /**
   * Generate stateless hash-based signature keys
   *
   * Uses SLH-DSA (FIPS 205) as backup signing algorithm
   */
  generateSLHKeys(): QuantumKeyPair {
    try {
      const keyPair = this.algorithms.slh.keygen();

      return {
        publicKey: keyPair.publicKey,
        privateKey: keyPair.secretKey,
        algorithm: 'SLH-DSA',
        securityLevel: this.securityLevel,
        keySize: keyPair.publicKey.length,
        timestamp: Date.now()
      };
    } catch (error) {
      throw new Error(`SLH-DSA key generation failed: ${error}`);
    }
  }

  /**
   * Encapsulate secret for quantum-safe key establishment
   *
   * @param publicKey - Recipient's ML-KEM public key
   * @returns Encapsulated key and shared secret
   */
  encapsulateSecret(publicKey: Uint8Array): { ciphertext: Uint8Array; sharedSecret: Uint8Array } {
    try {
      const result = this.algorithms.kem.encapsulate(publicKey);
      return {
        ciphertext: result.cipherText,
        sharedSecret: result.sharedSecret
      };
    } catch (error) {
      throw new Error(`ML-KEM encapsulation failed: ${error}`);
    }
  }

  /**
   * Decapsulate secret from quantum-safe key establishment
   *
   * @param ciphertext - Encapsulated key data
   * @param privateKey - Recipient's ML-KEM private key
   * @returns Shared secret
   */
  decapsulateSecret(ciphertext: Uint8Array, privateKey: Uint8Array): Uint8Array {
    try {
      return this.algorithms.kem.decapsulate(ciphertext, privateKey);
    } catch (error) {
      throw new Error(`ML-KEM decapsulation failed: ${error}`);
    }
  }

  /**
   * Sign data with ML-DSA (primary quantum-safe signature)
   *
   * @param message - Data to sign
   * @param privateKey - ML-DSA private key
   * @returns Quantum-safe signature
   */
  signWithDSA(message: Uint8Array, privateKey: Uint8Array): SignatureData {
    try {

      const signature = this.algorithms.dsa.sign(message, privateKey);

      return {
        signature,
        algorithm: 'ML-DSA',
        securityLevel: this.securityLevel,
        timestamp: Date.now(),
        messageHash: this.hashMessage(message)
      };
    } catch (error) {
      throw new Error(`ML-DSA signing failed: ${error}`);
    }
  }

  /**
   * Verify ML-DSA signature
   *
   * @param signature - Signature to verify
   * @param message - Original message
   * @param publicKey - ML-DSA public key
   * @returns True if signature is valid
   */
  verifyDSASignature(signature: Uint8Array, message: Uint8Array, publicKey: Uint8Array): boolean {
    try {
      return this.algorithms.dsa.verify(signature, message, publicKey);
    } catch (error) {
      console.error('ML-DSA verification failed:', error);
      return false;
    }
  }

  /**
   * Sign data with SLH-DSA (backup quantum-safe signature)
   *
   * @param message - Data to sign
   * @param privateKey - SLH-DSA private key
   * @returns Stateless hash-based signature
   */
  signWithSLH(message: Uint8Array, privateKey: Uint8Array): SignatureData {
    try {
      const signature = this.algorithms.slh.sign(message, privateKey);

      return {
        signature,
        algorithm: 'SLH-DSA',
        securityLevel: this.securityLevel,
        timestamp: Date.now(),
        messageHash: this.hashMessage(message)
      };
    } catch (error) {
      throw new Error(`SLH-DSA signing failed: ${error}`);
    }
  }

  /**
   * Verify SLH-DSA signature
   *
   * @param signature - Signature to verify
   * @param message - Original message
   * @param publicKey - SLH-DSA public key
   * @returns True if signature is valid
   */
  verifySLHSignature(signature: Uint8Array, message: Uint8Array, publicKey: Uint8Array): boolean {
    try {
      return this.algorithms.slh.verify(signature, message, publicKey);
    } catch (error) {
      console.error('SLH-DSA verification failed:', error);
      return false;
    }
  }

  /**
   * Encrypt data using quantum-safe hybrid approach
   *
   * Uses ML-KEM for key establishment + AES-256-GCM for data encryption
   *
   * @param data - Data to encrypt
   * @param recipientPublicKey - Recipient's ML-KEM public key
   * @returns Encrypted data package
   */
  async encryptData(data: Uint8Array, recipientPublicKey: Uint8Array): Promise<EncryptedData> {
    try {
      // Step 1: Generate ephemeral key pair and establish shared secret
      const { ciphertext: encapsulatedKey, sharedSecret } = this.encapsulateSecret(recipientPublicKey);

      // Step 2: Derive AES key from shared secret
      const aesKey = await this.deriveAESKey(sharedSecret);

      // Step 3: Generate random IV for AES-GCM
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Step 4: Encrypt data with AES-256-GCM
      const aesKeyObject = await crypto.subtle.importKey(
        'raw',
        toBufferSource(ensureArrayBuffer(aesKey)),
        { name: 'AES-GCM' },
        false,
        ['encrypt']
      );

      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        aesKeyObject,
        toBufferSource(ensureArrayBuffer(data))
      );

      const encryptedData = new Uint8Array(encryptedBuffer);

      return {
        encryptedData,
        encapsulatedKey,
        iv,
        algorithm: 'ML-KEM + AES-256-GCM',
        securityLevel: this.securityLevel,
        timestamp: Date.now()
      };
    } catch (error) {
      throw new Error(`Quantum-safe encryption failed: ${error}`);
    }
  }

  /**
   * Decrypt data using quantum-safe hybrid approach
   *
   * @param encryptedPackage - Encrypted data package
   * @param privateKey - Recipient's ML-KEM private key
   * @returns Decrypted data
   */
  async decryptData(encryptedPackage: EncryptedData, privateKey: Uint8Array): Promise<Uint8Array> {
    try {
      // Step 1: Decapsulate shared secret
      const sharedSecret = this.decapsulateSecret(encryptedPackage.encapsulatedKey, privateKey);

      // Step 2: Derive AES key from shared secret
      const aesKey = await this.deriveAESKey(sharedSecret);

      // Step 3: Decrypt data with AES-256-GCM
      const aesKeyObject = await crypto.subtle.importKey(
        'raw',
        toBufferSource(ensureArrayBuffer(aesKey)),
        { name: 'AES-GCM' },
        false,
        ['decrypt']
      );

      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: toBufferSource(ensureArrayBuffer(encryptedPackage.iv)) },
        aesKeyObject,
        toBufferSource(ensureArrayBuffer(encryptedPackage.encryptedData))
      );

      return new Uint8Array(decryptedBuffer);
    } catch (error) {
      throw new Error(`Quantum-safe decryption failed: ${error}`);
    }
  }

  /**
   * Derive AES key from ML-KEM shared secret using HKDF
   */
  private async deriveAESKey(sharedSecret: Uint8Array): Promise<Uint8Array> {
    // Import shared secret as HKDF key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      toBufferSource(ensureArrayBuffer(sharedSecret)),
      { name: 'HKDF' },
      false,
      ['deriveKey', 'deriveBits']
    );

    // Derive 256-bit AES key
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(32), // Zero salt for simplicity
        info: new TextEncoder().encode('QuantumSafeVault-AES-Key')
      },
      keyMaterial,
      256 // 256 bits for AES-256
    );

    return new Uint8Array(derivedBits);
  }

  /**
   * Create secure hash of message for signature verification
   */
  private hashMessage(message: Uint8Array): string {
    // Use a simple hash for now - in production, this would use SHA-3
    let hash = 0;
    for (let i = 0; i < Math.min(message.length, 1000); i++) {
      hash = ((hash << 5) - hash + message[i]) & 0xffffffff;
    }
    return hash.toString(16);
  }

  /**
   * Get current algorithm configuration
   */
  getAlgorithmInfo(): {
    securityLevel: SecurityLevel;
    algorithms: string[];
    description: string;
  } {
    return {
      securityLevel: this.securityLevel,
      algorithms: ['ML-KEM', 'ML-DSA', 'SLH-DSA'],
      description: this.algorithms.description
    };
  }

  /**
   * Generate complete quantum-safe key suite
   *
   * Creates all three key types for comprehensive quantum protection
   */
  generateCompleteKeySuite(): {
    kemKeys: QuantumKeyPair;
    dsaKeys: QuantumKeyPair;
    slhKeys: QuantumKeyPair;
    securityLevel: SecurityLevel;
  } {
    return {
      kemKeys: this.generateKEMKeys(),
      dsaKeys: this.generateDSAKeys(),
      slhKeys: this.generateSLHKeys(),
      securityLevel: this.securityLevel
    };
  }
}

/**
 * Factory function for creating NIST quantum-safe instances
 */
export function createQuantumSafe(securityLevel: SecurityLevel = 5): NISTQuantumSafe {
  return new NISTQuantumSafe(securityLevel);
}

/**
 * Utility function to validate security level
 */
export function isValidSecurityLevel(level: number): level is SecurityLevel {
  return level === 3 || level === 5;
}

/**
 * Get available algorithm information for UI display
 */
export function getAlgorithmSuites(): Array<{
  level: SecurityLevel;
  name: string;
  description: string;
  algorithms: string[];
}> {
  return [
    {
      level: 3,
      name: 'Category 3',
      description: 'AES-192 equivalent quantum resistance',
      algorithms: ['ML-KEM-768', 'ML-DSA-65', 'SLH-DSA-SHA2-192f']
    },
    {
      level: 5,
      name: 'Category 5',
      description: 'AES-256 equivalent quantum resistance',
      algorithms: ['ML-KEM-1024', 'ML-DSA-87', 'SLH-DSA-SHA2-256f']
    }
  ];
}